/**
 * Notifications Hook
 *
 * Real-time in-app notifications for users.
 * - Listens for new notifications via Firestore onSnapshot
 * - Provides unread count for badge display
 * - Allows marking notifications as read
 * - Allows deleting notifications
 */

import { useState, useEffect, useCallback } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  doc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  limit,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';

export interface Notification {
  id: string;
  userId: string;
  type: string;
  title: string;
  message: string;
  projectId?: string;
  projectName?: string;
  route?: string;
  pipelineId?: string;
  step?: string;
  data?: Record<string, unknown>;
  severity?: string;
  invitedBy?: string;
  invitedByName?: string;
  read: boolean;
  readAt?: Date;
  createdAt: Date;
}

const STEP_LABELS: Record<string, string> = {
  cim_analysis: 'GRID',
  sector_research: 'Sector Research',
  transaction_search: 'Transaction Search',
  ai_financial_model: 'AI Financial Model',
  ic_committee: 'IC Committee',
  ic_memo: 'IC Memo',
  ic_presentation: 'IC Presentation',
};

interface RawNotificationData {
  userId?: string;
  type?: string;
  title?: string;
  message?: string;
  body?: string;
  projectId?: string;
  projectName?: string;
  route?: string;
  actionUrl?: string;
  pipelineId?: string;
  step?: string;
  severity?: string;
  invitedBy?: string;
  invitedByName?: string;
  read?: boolean;
  readAt?: unknown;
  createdAt?: unknown;
  data?: {
    companyName?: unknown;
    error?: unknown;
  } & Record<string, unknown>;
}

function getStringValue(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function getNotificationText(data: RawNotificationData): { title: string; message: string } {
  if (data.title || data.message || data.body) {
    return {
      title: data.title || 'Notification',
      message: data.message || data.body || '',
    };
  }

  const companyName = getStringValue(data.data?.companyName) || data.projectName || 'this deal';
  const stepLabel = data.step ? STEP_LABELS[data.step] || data.step : 'Workflow';

  switch (data.type) {
    case 'pipeline_started':
      return {
        title: 'Rapid deal screen started',
        message: `Analysis started for ${companyName}.`,
      };
    case 'pipeline_completed':
      return {
        title: 'Rapid deal screen complete',
        message: `All selected analysis steps finished for ${companyName}.`,
      };
    case 'pipeline_cancelled':
      return {
        title: 'Rapid deal screen cancelled',
        message: `The active analysis workflow was cancelled for ${companyName}.`,
      };
    case 'step_completed':
      return {
        title: `${stepLabel} complete`,
        message: `${stepLabel} finished for ${companyName}.`,
      };
    case 'step_failed': {
      const errorMessage = getStringValue(data.data?.error);
      return {
        title: `${stepLabel} needs attention`,
        message: errorMessage
          ? errorMessage
          : `${stepLabel} failed for ${companyName}.`,
      };
    }
    case 'team_invite':
      return {
        title: 'Added to project',
        message: data.projectName
          ? `You were added to ${data.projectName}.`
          : 'You were added to a project.',
      };
    case 'mention':
      return {
        title: 'You were mentioned',
        message: data.message || 'A teammate mentioned you in a comment.',
      };
    case 'thread_reply':
      return {
        title: 'New thread reply',
        message: data.message || 'A teammate replied to a thread you participated in.',
      };
    default:
      return {
        title: 'Notification',
        message: '',
      };
  }
}

interface UseNotificationsReturn {
  notifications: Notification[];
  unreadCount: number;
  isLoading: boolean;
  error: string | null;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (notificationId: string) => Promise<void>;
  clearAll: () => Promise<void>;
}

export function useNotifications(maxNotifications = 50): UseNotificationsReturn {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Real-time listener for notifications
  useEffect(() => {
    if (!user?.uid) {
      setNotifications([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    const notificationsQuery = query(
      collection(db, 'notifications'),
      where('userId', '==', user.uid),
      orderBy('createdAt', 'desc'),
      limit(maxNotifications)
    );

    const unsubscribe = onSnapshot(
      notificationsQuery,
      (snapshot) => {
        const notificationsData = snapshot.docs.map((doc) => {
          const data = doc.data() as RawNotificationData;
          const text = getNotificationText(data);
          return {
            id: doc.id,
            userId: data.userId || '',
            type: data.type || 'system',
            title: text.title,
            message: text.message,
            projectId: data.projectId,
            projectName: data.projectName,
            route: data.route || data.actionUrl,
            pipelineId: data.pipelineId,
            step: data.step,
            data: data.data,
            severity: data.severity,
            invitedBy: data.invitedBy,
            invitedByName: data.invitedByName,
            read: data.read || false,
            readAt: data.readAt instanceof Timestamp ? data.readAt.toDate() : undefined,
            createdAt: data.createdAt instanceof Timestamp
              ? data.createdAt.toDate()
              : new Date(),
          } as Notification;
        });

        setNotifications(notificationsData);
        setIsLoading(false);
      },
      (err) => {
        console.error('Error fetching notifications:', err);
        setError(err.message);
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user?.uid, maxNotifications]);

  // Calculate unread count
  const unreadCount = notifications.filter((n) => !n.read).length;

  // Mark a single notification as read
  const markAsRead = useCallback(async (notificationId: string) => {
    if (!user?.uid) return;

    try {
      const notificationRef = doc(db, 'notifications', notificationId);
      await updateDoc(notificationRef, {
        read: true,
        readAt: serverTimestamp(),
      });
    } catch (err) {
      console.error('Error marking notification as read:', err);
      throw err;
    }
  }, [user?.uid]);

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    if (!user?.uid) return;

    const unreadNotifications = notifications.filter((n) => !n.read);

    try {
      await Promise.all(
        unreadNotifications.map((notification) =>
          updateDoc(doc(db, 'notifications', notification.id), {
            read: true,
            readAt: serverTimestamp(),
          })
        )
      );
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
      throw err;
    }
  }, [user?.uid, notifications]);

  // Delete a single notification
  const deleteNotification = useCallback(async (notificationId: string) => {
    if (!user?.uid) return;

    try {
      await deleteDoc(doc(db, 'notifications', notificationId));
    } catch (err) {
      console.error('Error deleting notification:', err);
      throw err;
    }
  }, [user?.uid]);

  // Clear all notifications
  const clearAll = useCallback(async () => {
    if (!user?.uid) return;

    try {
      await Promise.all(
        notifications.map((notification) =>
          deleteDoc(doc(db, 'notifications', notification.id))
        )
      );
    } catch (err) {
      console.error('Error clearing all notifications:', err);
      throw err;
    }
  }, [user?.uid, notifications]);

  return {
    notifications,
    unreadCount,
    isLoading,
    error,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    clearAll,
  };
}
